import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  BarChart3, 
  TrendingUp, 
  AlertCircle, 
  CheckCircle, 
  Clock, 
  Target,
  Users,
  BookOpen,
  Palette,
  RefreshCw
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface HealthMetrics {
  completenessScore: number;
  consistencyScore: number;
  pacingScore: number;
  characterDevelopmentScore: number;
  plotCoherenceScore: number;
  overallHealthScore: number;
  lastCalculated: string;
}

interface Analysis {
  id: string;
  analysisType: string;
  analysisData: any;
  confidenceScore: number;
  createdAt: string;
}

interface Recommendation {
  type: string;
  priority: string;
  title: string;
  description: string;
  action: string;
}

interface StoryHealthDashboardProps {
  projectId: number;
}

export function StoryHealthDashboard({ projectId }: StoryHealthDashboardProps) {
  const [healthMetrics, setHealthMetrics] = useState<HealthMetrics | null>(null);
  const [recentAnalysis, setRecentAnalysis] = useState<Analysis[]>([]);
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [pendingSuggestions, setPendingSuggestions] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadHealthDashboard();
  }, [projectId]);

  const loadHealthDashboard = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`/api/analysis/dashboard/${projectId}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setHealthMetrics(data.healthMetrics);
        setRecentAnalysis(data.recentAnalysis || []);
        setPendingSuggestions(data.pendingSuggestions || 0);
        
        // Load recommendations
        const recResponse = await fetch(`/api/analysis/recommendations/${projectId}`, {
          method: 'POST',
          credentials: 'include'
        });
        
        if (recResponse.ok) {
          const recData = await recResponse.json();
          setRecommendations(recData.recommendations || []);
        }
      } else {
        toast({
          title: "Error",
          description: "Failed to load story health dashboard",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error loading dashboard:', error);
      toast({
        title: "Error",
        description: "Failed to load story health dashboard",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const runAnalysis = async (type: 'plot' | 'consistency') => {
    setIsAnalyzing(true);
    try {
      const response = await fetch(`/api/analysis/${type}/${projectId}`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        toast({
          title: "Analysis Complete",
          description: `${type.charAt(0).toUpperCase() + type.slice(1)} analysis completed successfully`
        });
        loadHealthDashboard();
      } else {
        const error = await response.json();
        toast({
          title: "Analysis Failed",
          description: error.message || `Failed to analyze ${type}`,
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error(`Error analyzing ${type}:`, error);
      toast({
        title: "Error",
        description: `Failed to analyze ${type}`,
        variant: "destructive"
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 0.8) return 'text-green-600';
    if (score >= 0.6) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreLabel = (score: number) => {
    if (score >= 0.8) return 'Excellent';
    if (score >= 0.6) return 'Good';
    if (score >= 0.4) return 'Needs Work';
    return 'Critical';
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'destructive';
      case 'medium': return 'default';
      case 'low': return 'secondary';
      default: return 'outline';
    }
  };

  const formatAnalysisType = (type: string) => {
    switch (type) {
      case 'plot': return 'Plot Structure';
      case 'consistency': return 'Story Consistency';
      case 'pacing': return 'Pacing Analysis';
      case 'character': return 'Character Development';
      default: return type.charAt(0).toUpperCase() + type.slice(1);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center py-8 text-muted-foreground">
            Loading story health dashboard...
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Health Overview */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Story Health Overview
          </CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={loadHealthDashboard}
            disabled={isAnalyzing}
          >
            <RefreshCw className={`h-4 w-4 ${isAnalyzing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </CardHeader>
        <CardContent className="space-y-6">
          {healthMetrics ? (
            <>
              {/* Overall Score */}
              <div className="text-center space-y-2">
                <div className="text-3xl font-bold text-primary">
                  {Math.round(healthMetrics.overallHealthScore * 100)}%
                </div>
                <div className={`text-lg font-medium ${getScoreColor(healthMetrics.overallHealthScore)}`}>
                  {getScoreLabel(healthMetrics.overallHealthScore)}
                </div>
                <div className="text-sm text-muted-foreground">
                  Overall Story Health
                </div>
              </div>

              {/* Detailed Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="flex items-center gap-2">
                      <CheckCircle className="h-3 w-3" />
                      Completeness
                    </span>
                    <span className={getScoreColor(healthMetrics.completenessScore)}>
                      {Math.round(healthMetrics.completenessScore * 100)}%
                    </span>
                  </div>
                  <Progress value={healthMetrics.completenessScore * 100} className="h-2" />
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="flex items-center gap-2">
                      <Target className="h-3 w-3" />
                      Consistency
                    </span>
                    <span className={getScoreColor(healthMetrics.consistencyScore)}>
                      {Math.round(healthMetrics.consistencyScore * 100)}%
                    </span>
                  </div>
                  <Progress value={healthMetrics.consistencyScore * 100} className="h-2" />
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="flex items-center gap-2">
                      <Clock className="h-3 w-3" />
                      Pacing
                    </span>
                    <span className={getScoreColor(healthMetrics.pacingScore)}>
                      {Math.round(healthMetrics.pacingScore * 100)}%
                    </span>
                  </div>
                  <Progress value={healthMetrics.pacingScore * 100} className="h-2" />
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="flex items-center gap-2">
                      <Users className="h-3 w-3" />
                      Character Development
                    </span>
                    <span className={getScoreColor(healthMetrics.characterDevelopmentScore)}>
                      {Math.round(healthMetrics.characterDevelopmentScore * 100)}%
                    </span>
                  </div>
                  <Progress value={healthMetrics.characterDevelopmentScore * 100} className="h-2" />
                </div>
              </div>

              <div className="text-xs text-muted-foreground text-center">
                Last calculated: {new Date(healthMetrics.lastCalculated).toLocaleString()}
              </div>
            </>
          ) : (
            <div className="text-center py-8 space-y-4">
              <AlertCircle className="h-12 w-12 mx-auto text-muted-foreground" />
              <div className="space-y-2">
                <div className="font-medium">No health metrics available</div>
                <div className="text-sm text-muted-foreground">
                  Run story analysis to generate health metrics
                </div>
              </div>
              <div className="flex justify-center gap-2">
                <Button
                  onClick={() => runAnalysis('plot')}
                  disabled={isAnalyzing}
                  size="sm"
                >
                  Analyze Plot
                </Button>
                <Button
                  onClick={() => runAnalysis('consistency')}
                  disabled={isAnalyzing}
                  size="sm"
                  variant="outline"
                >
                  Check Consistency
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detailed Analysis */}
      <Tabs defaultValue="recommendations" className="space-y-4">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="recommendations">
            Recommendations ({recommendations.length})
          </TabsTrigger>
          <TabsTrigger value="analysis">
            Recent Analysis ({recentAnalysis.length})
          </TabsTrigger>
          <TabsTrigger value="suggestions">
            AI Suggestions ({pendingSuggestions})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="recommendations">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Story Recommendations</CardTitle>
            </CardHeader>
            <CardContent>
              {recommendations.length === 0 ? (
                <div className="text-center py-4 text-muted-foreground">
                  No recommendations available
                </div>
              ) : (
                <ScrollArea className="h-64">
                  <div className="space-y-3">
                    {recommendations.map((rec, index) => (
                      <div key={index} className="border rounded p-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="font-medium">{rec.title}</div>
                          <Badge variant={getPriorityColor(rec.priority)}>
                            {rec.priority}
                          </Badge>
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {rec.description}
                        </div>
                        <Badge variant="outline" className="text-xs">
                          {rec.type}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="analysis">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Recent Analysis Results</CardTitle>
            </CardHeader>
            <CardContent>
              {recentAnalysis.length === 0 ? (
                <div className="text-center py-4 text-muted-foreground">
                  No recent analysis available
                </div>
              ) : (
                <ScrollArea className="h-64">
                  <div className="space-y-3">
                    {recentAnalysis.map(analysis => (
                      <div key={analysis.id} className="border rounded p-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="font-medium">
                            {formatAnalysisType(analysis.analysisType)}
                          </div>
                          <Badge variant="outline">
                            {Math.round(analysis.confidenceScore * 100)}% confidence
                          </Badge>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {new Date(analysis.createdAt).toLocaleString()}
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="suggestions">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Pending AI Suggestions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center py-4">
                <div className="text-2xl font-bold text-primary">{pendingSuggestions}</div>
                <div className="text-sm text-muted-foreground">
                  AI suggestions awaiting review
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}